import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FiArrowLeft, FiCalendar, FiClock, FiCheckCircle, FiXCircle,
  FiAlertCircle, FiCheck, FiX, FiRefreshCw, FiPrinter, FiUser,
  FiBriefcase, FiTrendingDown, FiShield, FiFileText, FiLayers,
  FiAlertTriangle, FiInfo, FiChevronRight, FiChevronLeft, FiList, FiDollarSign
} from 'react-icons/fi';
import ConfirmModal from '../shared/ConfirmModal';
import { fmtDate } from '../../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const initials = (name = '') =>
  name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '??';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Robust date helpers to avoid timezone off-by-one shifts
const toDateStr = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val.split('T')[0];
  if (val instanceof Date) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
};

const parseLocalDate = (str) => {
  if (!str) return new Date();
  const clean = String(str).split('T')[0];
  const parts = clean.split('-');
  if (parts.length === 3) {
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }
  return new Date(str);
};

export default function HrLeaveDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize with passed state if available for instant render
  const [leave, setLeave] = useState(location.state?.leave || null);
  const [balanceData, setBalanceData] = useState(null);
  const [employeeLeaves, setEmployeeLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(!location.state?.leave);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [error, setError] = useState('');

  // Action modal
  const [confirm, setConfirm] = useState({ open: false, status: null, loading: false });
  const [actionRemark, setActionRemark] = useState('');

  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isSuperadmin = ['SUPERADMIN', 'TESTER'].includes(String(currentUser.role || '').toUpperCase());

  // Track holidays fetch to ensure it only fires once on mount
  const holidaysFetchedRef = React.useRef(false);

  // Helper to load leave details and subordinate balance/leaves
  const loadLeaveData = useCallback(async (targetId, isInitial = false) => {
    if (!targetId) return null;
    try {
      if (isInitial && !location.state?.leave) {
        setLoading(true);
      }
      setError('');
      const res = await api.get(`/leave/${targetId}`);
      const data = res.data?.data ?? res.data;
      setLeave(data);

      if (data && data.employeeId) {
        setBalanceLoading(true);
        const yr = data.startDate ? parseLocalDate(data.startDate).getFullYear() : new Date().getFullYear();
        const [balRes, subRes] = await Promise.allSettled([
          api.get(`/leave/subordinate/${data.employeeId}/monthly-balance/${yr}`),
          api.get(`/leave/subordinate/${data.employeeId}`)
        ]);

        if (balRes.status === 'fulfilled') {
          setBalanceData(balRes.value.data?.data ?? balRes.value.data);
        }
        if (subRes.status === 'fulfilled') {
          const list = subRes.value.data?.data ?? subRes.value.data ?? [];
          if (Array.isArray(list)) setEmployeeLeaves(list);
        }
      }
      return data;
    } catch (err) {
      console.error('Failed to load leave details', err);
      setError('Could not load leave application details.');
      toast.error('Failed to load leave details');
      return null;
    } finally {
      setLoading(false);
      setBalanceLoading(false);
    }
  }, [location.state?.leave]);

  // Fetch official holidays once on mount
  useEffect(() => {
    if (holidaysFetchedRef.current) return;
    holidaysFetchedRef.current = true;
    api.get('/holidays')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        setHolidays(list);
      })
      .catch(err => console.warn('Could not fetch holidays', err));
  }, []);

  // Fetch leave details strictly when `id` changes
  useEffect(() => {
    loadLeaveData(id, true);
  }, [id, loadLeaveData]);

  // Handle Approve / Reject / Reverse
  const handleAction = async () => {
    if (!leave) return;
    setConfirm(p => ({ ...p, loading: true }));
    try {
      if (confirm.status === 'REVERSE') {
        await api.put(`/leave/${leave.id}/reverse-rejection`, { remarks: actionRemark.trim() });
        toast.success('Rejection reversed successfully');
      } else {
        const remarks = actionRemark.trim() || (confirm.status === 'APPROVED' ? 'Approved by HR' : 'Rejected by HR');
        await api.put('/leave/approve', { leaveId: leave.id, status: confirm.status, remarks });
        toast.success(`Leave ${confirm.status.toLowerCase()} successfully`);
      }
      setConfirm({ open: false, status: null, loading: false });
      await loadLeaveData(leave.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
      setConfirm(p => ({ ...p, loading: false }));
    }
  };

  // Day-by-day calculation for current leave
  const dayBreakdown = useMemo(() => {
    if (!leave?.startDate || !leave?.endDate) return [];
    const days = [];
    const curr = parseLocalDate(leave.startDate);
    const end = parseLocalDate(leave.endDate);

    let paidRemaining = leave.paidDays || 0;

    while (curr <= end) {
      const dayOfWeekIdx = curr.getDay(); // 0 = Sun, 6 = Sat
      const isWeekend = dayOfWeekIdx === 0 || dayOfWeekIdx === 6;
      const dateStr = toDateStr(curr);

      let isPaid = false;
      if (paidRemaining > 0) {
        isPaid = true;
        paidRemaining--;
      }

      days.push({
        dateStr,
        dayName: DAYS_OF_WEEK[dayOfWeekIdx],
        isWeekend,
        isSaturday: dayOfWeekIdx === 6,
        isSunday: dayOfWeekIdx === 0,
        isPaid,
        isLop: !isPaid,
      });

      curr.setDate(curr.getDate() + 1);
    }
    return days;
  }, [leave?.startDate, leave?.endDate, leave?.paidDays]);

  const weekendCount = useMemo(() => {
    return dayBreakdown.filter(d => d.isWeekend).length;
  }, [dayBreakdown]);

  const workingDaysCount = useMemo(() => {
    return dayBreakdown.filter(d => !d.isWeekend).length;
  }, [dayBreakdown]);

  // Aggregate all previous leaves of this employee (excluding currently viewed leave)
  const previousLeaves = useMemo(() => {
    const map = new Map();
    const currentId = leave?.id ? String(leave.id) : null;

    const addLeave = (l) => {
      if (!l || !l.id) return;
      const idStr = String(l.id);
      if (idStr === currentId || map.has(idStr)) return;
      map.set(idStr, l);
    };

    (employeeLeaves || []).forEach(addLeave);
    (balanceData?.approvedRequests || []).forEach(addLeave);
    (balanceData?.pendingRequests || []).forEach(addLeave);

    return Array.from(map.values());
  }, [employeeLeaves, balanceData, leave?.id]);

  // Calendar View State & Navigation
  const [scheduleView, setScheduleView] = useState('calendar'); // 'calendar' | 'list'
  const [calDate, setCalDate] = useState(() => {
    return leave?.startDate ? parseLocalDate(leave.startDate) : new Date();
  });

  useEffect(() => {
    if (leave?.startDate) {
      setCalDate(parseLocalDate(leave.startDate));
    }
  }, [leave?.startDate]);

  const changeMonth = (offset) => {
    setCalDate(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + offset);
      return next;
    });
  };

  // Get previous leaves in a specific month
  const getPrevLeavesInMonth = useCallback((targetDate) => {
    if (!targetDate) return [];
    const y = targetDate.getFullYear();
    const m = targetDate.getMonth();
    const mStart = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const mEnd = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    return previousLeaves.filter(l => {
      if (l.status !== 'APPROVED') return false;
      const s = toDateStr(l.startDate);
      const e = toDateStr(l.endDate);
      return s && e && s <= mEnd && e >= mStart;
    });
  }, [previousLeaves]);

  // Generate calendar grid cells for any month
  const generateMonthCells = useCallback((targetDate) => {
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();

    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 = Sun, 6 = Sat
    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells = [];

    const toDateKey = (y, m, d) => {
      const mm = String(m + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    };

    const getPrevLeave = (dateStr) => {
      const approved = previousLeaves.find(l => {
        if (l.status !== 'APPROVED') return false;
        const s = toDateStr(l.startDate);
        const e = toDateStr(l.endDate);
        return s && e && dateStr >= s && dateStr <= e;
      });
      if (approved) return approved;

      return previousLeaves.find(l => {
        if (l.status !== 'PENDING') return false;
        const s = toDateStr(l.startDate);
        const e = toDateStr(l.endDate);
        return s && e && dateStr >= s && dateStr <= e;
      });
    };

    const getHoliday = (dateStr) => {
      return (holidays || []).find(h => {
        const hd = toDateStr(h.date || h.holidayDate);
        return hd === dateStr;
      });
    };

    // Previous month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const d = new Date(year, month - 1, dayNum);
      const dateStr = toDateKey(d.getFullYear(), d.getMonth(), dayNum);
      const leaveDay = dayBreakdown.find(item => item.dateStr === dateStr);
      const prevLeave = getPrevLeave(dateStr);
      const holiday = getHoliday(dateStr);

      cells.push({
        dateStr,
        dayNum,
        isCurrentMonth: false,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        leaveDay,
        prevLeave,
        holiday,
      });
    }

    // Current month days
    for (let dayNum = 1; dayNum <= daysInCurrentMonth; dayNum++) {
      const d = new Date(year, month, dayNum);
      const dateStr = toDateKey(year, month, dayNum);
      const leaveDay = dayBreakdown.find(item => item.dateStr === dateStr);
      const prevLeave = getPrevLeave(dateStr);
      const holiday = getHoliday(dateStr);

      cells.push({
        dateStr,
        dayNum,
        isCurrentMonth: true,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        leaveDay,
        prevLeave,
        holiday,
      });
    }

    // Next month padding to complete grid
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      const dateStr = toDateKey(d.getFullYear(), d.getMonth(), i);
      const leaveDay = dayBreakdown.find(item => item.dateStr === dateStr);
      const prevLeave = getPrevLeave(dateStr);
      const holiday = getHoliday(dateStr);

      cells.push({
        dateStr,
        dayNum: i,
        isCurrentMonth: false,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        leaveDay,
        prevLeave,
        holiday,
      });
    }

    return cells;
  }, [previousLeaves, dayBreakdown, holidays]);

  const calendarCells = useMemo(() => generateMonthCells(calDate), [generateMonthCells, calDate]);
  const prevInThisMonth = useMemo(() => getPrevLeavesInMonth(calDate), [getPrevLeavesInMonth, calDate]);
  const isLeaveMonth = useMemo(() => {
    return leave?.startDate &&
      calDate.getMonth() === parseLocalDate(leave.startDate).getMonth() &&
      calDate.getFullYear() === parseLocalDate(leave.startDate).getFullYear();
  }, [leave?.startDate, calDate]);

  if (loading && !leave) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-500">
        <FiRefreshCw className="animate-spin text-sky-500" size={28} />
        <p className="text-sm font-semibold">Loading leave details...</p>
      </div>
    );
  }

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (window.history.length > 1 && location.key !== 'default') {
      navigate(-1);
    } else if (location.pathname.startsWith('/hr/')) {
      navigate('/hr/all-leaves');
    } else if (location.pathname.startsWith('/manager/')) {
      navigate('/manager/leave-approvals');
    } else {
      navigate('/leaves-wfh');
    }
  };

  const backLabel = location.pathname.startsWith('/hr/')
    ? 'Back to Leave Records'
    : location.pathname.startsWith('/manager/')
    ? 'Back to Leave Approvals'
    : 'Back to Leave Requests';

  if (error && !leave) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-slate-600">
        <FiAlertCircle size={36} className="text-rose-500" />
        <p className="text-base font-semibold">{error}</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 text-xs font-bold rounded-xl border border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer"
        >
          &larr; {backLabel}
        </button>
      </div>
    );
  }

  const name = leave?.employeeName || leave?.employeeUsername || `#${leave?.employeeId}`;
  const isPending = leave?.status === 'PENDING';
  const isApproved = leave?.status === 'APPROVED';
  const isRejected = leave?.status === 'REJECTED';

  const statusConfig = {
    PENDING:   { label: 'Pending Approval', bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6', border: '#bfdbfe' },
    APPROVED:  { label: 'Approved',         bg: '#f0fdf4', color: '#15803d', dot: '#22c55e', border: '#bbf7d0' },
    REJECTED:  { label: 'Rejected',         bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', border: '#fecaca' },
    CANCELLED: { label: 'Cancelled',        bg: '#f9fafb', color: '#6b7280', dot: '#9ca3af', border: '#e5e7eb' },
  };
  const sc = statusConfig[leave?.status] || statusConfig.PENDING;

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* ── Top Navigation & Breadcrumbs ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-sky-600 transition-colors mb-2 cursor-pointer group"
          >
            <FiArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" /> {backLabel}
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              Leave Application <span className="text-sky-600 font-mono">#LEAVE-{leave.id}</span>
            </h1>
          </div>
        </div>

        {/* Top Actions */}
        <div className="grid grid-cols-2 sm:flex items-center gap-2.5 w-full sm:w-auto">
          <button
            onClick={() => window.print()}
            className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition-all shadow-sm cursor-pointer"
          >
            <FiPrinter size={14} /> Print / Export
          </button>

          {isPending && (
            <>
              <button
                onClick={() => { setActionRemark(''); setConfirm({ open: true, status: 'APPROVED', loading: false }); }}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white shadow-sm hover:opacity-95 transition-all cursor-pointer"
                style={{ background: '#1ba8db' }}
              >
                <FiCheck size={14} /> Approve Leave
              </button>
              <button
                onClick={() => { setActionRemark(''); setConfirm({ open: true, status: 'REJECTED', loading: false }); }}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 transition-all shadow-sm cursor-pointer"
              >
                <FiX size={14} /> Reject Leave
              </button>
            </>
          )}

          {isRejected && isSuperadmin && (
            <button
              onClick={() => { setActionRemark(''); setConfirm({ open: true, status: 'REVERSE', loading: false }); }}
              className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 transition-all shadow-sm cursor-pointer"
            >
              <FiRefreshCw size={14} /> Reverse Rejection
            </button>
          )}
        </div>
      </div>

      {/* ── Main Leave Header Card ── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-100">
          {/* Employee Avatar & Basic Info */}
          <div className="flex items-start sm:items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-sm shrink-0"
              style={{ background: '#1ba8db' }}
            >
              {initials(name)}
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap mb-1">
                <h2 className="text-xl font-bold text-slate-900">{name}</h2>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border"
                  style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot, animation: isPending ? 'pulse 2s infinite' : 'none' }} />
                  {sc.label}
                </span>
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-sm"
                  style={{ background: '#eff6ff', color: '#1ba8db', borderColor: '#bfdbfe' }}
                >
                  {leave.leaveType}
                </span>
                {leave.isHalfDay && (
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-sm bg-indigo-50 text-indigo-700 border-indigo-200">
                    Half Day ({leave.halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})
                  </span>
                )}
                {(leave.unpaidDays || 0) > 0 && (
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full text-white bg-slate-900 shadow-sm">
                    LOP ({leave.unpaidDays}d)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded font-semibold text-slate-700">#{leave.employeeId}</span>
                {leave.jobTitle && <span className="font-medium text-slate-700">{leave.jobTitle}</span>}
                {leave.jobTitle && leave.department && <span className="text-slate-300">•</span>}
                {leave.department && <span className="font-medium text-slate-600">{leave.department}</span>}
                {leave.employeeUsername && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-400">@{leave.employeeUsername}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Submission & Audit Timestamps */}
          <div className="flex flex-col sm:flex-row lg:flex-col lg:items-end gap-2 text-xs text-slate-500">
            {leave.appliedDate && (
              <div className="flex items-center gap-1.5">
                <FiClock size={13} className="text-slate-400" />
                <span>Applied: <strong className="text-slate-800 font-semibold">{fmtDate(leave.appliedDate)}</strong></span>
              </div>
            )}
            {leave.approvedDate && (
              <div className="flex items-center gap-1.5">
                <FiCheckCircle size={13} className={isApproved ? 'text-emerald-600' : 'text-rose-600'} />
                <span>
                  {isApproved ? 'Approved by' : 'Processed by'}:{' '}
                  <strong className="text-slate-800 font-semibold">{leave.approvedByUsername || 'HR Admin'}</strong> on{' '}
                  <strong className="text-slate-800 font-semibold">{fmtDate(leave.approvedDate)}</strong>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── 3 High-Impact Metric Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Total Requested</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-slate-900">{leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : 1)}</span>
              <span className="text-xs font-semibold text-slate-500">{leave.isHalfDay ? 'day' : 'days'}</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Calendar span of application</p>
          </div>

          <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-4">
            <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Paid Days</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-emerald-800">{leave.paidDays ?? 0}</span>
              <span className="text-xs font-semibold text-emerald-600">days</span>
            </div>
            <p className="text-[11px] text-emerald-600/90 mt-1">Covered with full pay quota</p>
          </div>

          <div className="bg-rose-50/60 border border-rose-200/80 rounded-xl p-4">
            <p className="text-[11px] font-bold text-rose-700 uppercase tracking-wider mb-1">Loss of Pay (LOP)</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-rose-800">{leave.unpaidDays ?? 0}</span>
              <span className="text-xs font-semibold text-rose-600">days</span>
            </div>
            <p className="text-[11px] text-rose-600/90 mt-1">
              {(leave.unpaidDays || 0) > 0 ? 'Salary deduction applies' : 'No salary deduction'}
            </p>
          </div>
        </div>
      </div>

      {/* ── 2 Columns: Calendar Schedule (Left) & Quotas / History (Right) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* LEFT COLUMN: Interactive Calendar Schedule & Reason / Remarks (7 cols) */}
        <div className="lg:col-span-7 space-y-6">

          {/* Date Range & Calendar Breakdown */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            {/* Header: Title, Navigation & View Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FiCalendar className="text-sky-600" size={18} />
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Leave Calendar Schedule</h3>
                  <p className="text-xs text-slate-500 font-medium">{fmtDate(leave.startDate)} &rarr; {fmtDate(leave.endDate)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold">
                  <button
                    onClick={() => setScheduleView('calendar')}
                    className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${scheduleView === 'calendar' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    <FiCalendar size={12} /> Calendar
                  </button>
                  <button
                    onClick={() => setScheduleView('list')}
                    className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${scheduleView === 'list' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    <FiList size={12} /> List
                  </button>
                </div>
              </div>
            </div>

            {/* CALENDAR VIEW */}
            {scheduleView === 'calendar' && (
              <div className="pt-4 space-y-4">
                {/* Single Month Navigator & Info Bar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => changeMonth(-1)}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                      title="Previous month"
                    >
                      <FiChevronLeft size={16} />
                    </button>
                    <span className="font-bold text-slate-900 text-sm min-w-[140px] text-center">
                      {calDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => changeMonth(1)}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                      title="Next month"
                    >
                      <FiChevronRight size={16} />
                    </button>
                    {isLeaveMonth && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-sky-100 text-sky-700 border border-sky-200 uppercase tracking-tight ml-1">
                        Current Request
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {prevInThisMonth.length > 0 && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-1.5 shadow-xs">
                        <span className="w-2 h-2 rounded-full bg-indigo-600" />
                        {prevInThisMonth.length} Previous Leave ({prevInThisMonth.reduce((acc, l) => acc + (l.daysCount || 0), 0)} days)
                      </span>
                    )}
                    {!isLeaveMonth && leave?.startDate && (
                      <button
                        onClick={() => setCalDate(parseLocalDate(leave.startDate))}
                        className="text-xs font-bold px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-sky-600 hover:bg-sky-50 transition-colors cursor-pointer"
                      >
                        Back to Leave Month
                      </button>
                    )}
                  </div>
                </div>

                {/* Days of Week Header */}
                <div className="grid grid-cols-7 gap-1.5 text-center text-xs font-bold uppercase tracking-wider text-slate-400 py-1 border-b border-slate-100">
                  <span className="text-rose-500">Sun</span>
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span className="text-amber-500">Sat</span>
                </div>

                {/* Calendar Cells */}
                <div className="grid grid-cols-7 gap-1.5">
                  {calendarCells.map((cell, idx) => {
                    const { isCurrentMonth, dayNum, isWeekend, leaveDay, prevLeave, holiday, dateStr } = cell;

                    // Non-current month padding
                    if (!isCurrentMonth) {
                      if (prevLeave && prevLeave.status === 'APPROVED') {
                        return (
                          <div
                            key={`pad-${idx}`}
                            title={`Previous Leave: ${prevLeave.leaveType} (${fmtDate(prevLeave.startDate)} - ${fmtDate(prevLeave.endDate)})`}
                            onClick={() => navigate(`/hr/leave/${prevLeave.id}`)}
                            className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl bg-indigo-50/40 border border-indigo-100/60 opacity-60 flex flex-col justify-between cursor-pointer hover:opacity-100 transition-all"
                          >
                            <span className="font-bold text-[11px] text-indigo-700">{dayNum}</span>
                            <span className="text-[9px] font-bold text-indigo-600 truncate">Prev: {prevLeave.leaveType}</span>
                          </div>
                        );
                      }
                      if (leaveDay) {
                        return (
                          <div
                            key={`pad-${idx}`}
                            className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl bg-emerald-50/40 border border-emerald-100/60 opacity-60 flex flex-col justify-between"
                          >
                            <span className="font-bold text-[11px] text-emerald-700">{dayNum}</span>
                            <span className="text-[9px] font-bold text-emerald-600">Leave</span>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={`pad-${idx}`}
                          className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl bg-slate-50/30 border border-transparent text-slate-300 text-xs flex flex-col justify-between select-none"
                        >
                          <span className="font-semibold text-[11px] opacity-40">{dayNum}</span>
                        </div>
                      );
                    }

                    // Case 1: Currently viewed leave request day
                    if (leaveDay) {
                      const isPaid = leaveDay.isPaid;

                      let cellBg = isPaid
                        ? 'bg-emerald-50/90 border-emerald-300 ring-2 ring-emerald-500/20'
                        : 'bg-rose-50/90 border-rose-300 ring-2 ring-rose-500/20';
                      let numColor = isPaid ? 'text-emerald-950' : 'text-rose-950';

                      return (
                        <div
                          key={dateStr}
                          className={`min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border flex flex-col justify-between transition-all shadow-xs ${cellBg}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-black ${numColor}`}>{dayNum}</span>
                          </div>

                          <div className="mt-1 flex flex-col gap-0.5">
                            {isPaid ? (
                              <span className="inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-600 text-white shadow-xs">
                                <FiCheck size={10} /> Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-rose-600 text-white shadow-xs">
                                <FiX size={10} /> LOP
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Case 2: Employee's Previous Approved Leave
                    if (prevLeave && prevLeave.status === 'APPROVED') {
                      const isUnpaid = (prevLeave.unpaidDays || 0) > 0 && (prevLeave.paidDays || 0) === 0;
                      return (
                        <div
                          key={dateStr}
                          onClick={() => navigate(`/hr/leave/${prevLeave.id}`)}
                          title={`Past Approved Leave #LEAVE-${prevLeave.id}: ${prevLeave.leaveType} (${fmtDate(prevLeave.startDate)} - ${fmtDate(prevLeave.endDate)}) • Click to view`}
                          className={`min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border flex flex-col justify-between transition-all shadow-xs hover:shadow-sm cursor-pointer ${
                            isUnpaid ? 'border-slate-300 bg-slate-100/90 text-slate-700' : 'border-slate-300 bg-slate-100/80 text-slate-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-black text-slate-800">{dayNum}</span>
                            <span className="text-[8px] font-bold uppercase px-1 py-0.2 rounded bg-slate-200 text-slate-600 tracking-tight">
                              Past
                            </span>
                          </div>

                          <div className="mt-1 flex flex-col gap-0.5">
                            <span className="inline-flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-slate-500 text-white shadow-xs truncate">
                              <FiCheck size={9} /> {prevLeave.leaveType || 'Leave'}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    // Case 2b: Employee's Previous Pending Leave
                    if (prevLeave && prevLeave.status === 'PENDING') {
                      return (
                        <div
                          key={dateStr}
                          onClick={() => navigate(`/hr/leave/${prevLeave.id}`)}
                          title={`Other Pending Leave #LEAVE-${prevLeave.id}: ${prevLeave.leaveType} (${fmtDate(prevLeave.startDate)} - ${fmtDate(prevLeave.endDate)})`}
                          className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-amber-300 bg-amber-50/80 ring-2 ring-amber-500/10 flex flex-col justify-between transition-all cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-amber-950">{dayNum}</span>
                            <span className="text-[8px] font-bold uppercase px-1 py-0.2 rounded bg-amber-100 text-amber-800">
                              Pending
                            </span>
                          </div>
                          <div className="mt-1">
                            <span className="inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-600 text-white truncate">
                              <FiClock size={9} /> {prevLeave.leaveType}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    // Case 3: Company Holiday
                    if (holiday) {
                      return (
                        <div
                          key={dateStr}
                          title={`Holiday: ${holiday.name || 'Holiday'}`}
                          className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-teal-200 bg-teal-50/70 text-teal-900 flex flex-col justify-between transition-colors"
                        >
                          <span className="text-xs font-bold text-teal-800">{dayNum}</span>
                          <span className="text-[9px] font-bold text-teal-700 truncate" title={holiday.name}>
                            🎉 {holiday.name || 'Holiday'}
                          </span>
                        </div>
                      );
                    }

                    // Case 4: Weekend / Off-day
                    if (isWeekend) {
                      return (
                        <div
                          key={dateStr}
                          className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-slate-200/50 bg-slate-50/70 text-slate-400 flex flex-col justify-between"
                        >
                          <span className="text-xs font-bold text-slate-400">{dayNum}</span>
                          <span className="text-[9px] font-semibold text-slate-400">Off</span>
                        </div>
                      );
                    }

                    // Case 5: Normal working day
                    return (
                      <div
                        key={dateStr}
                        className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-slate-200/80 bg-white text-slate-700 flex flex-col justify-between hover:bg-slate-50/50 transition-colors"
                      >
                        <span className="text-xs font-bold text-slate-700">{dayNum}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Calendar Legend */}
                <div className="flex items-center gap-x-5 gap-y-2 pt-3 border-t border-slate-100 flex-wrap text-xs font-medium text-slate-600">
                  <span className="text-slate-400 text-[11px] uppercase tracking-wider font-bold mr-1">Legend:</span>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-slate-700 font-semibold">Paid Leave</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0" />
                    <span className="text-slate-700 font-semibold">LOP / Unpaid Leave</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-400 shrink-0" />
                    <span className="text-slate-700 font-semibold">Past Leave</span>
                  </div>
                  <div className="flex items-center gap-2 text-teal-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-teal-500 shrink-0" />
                    <span>Holiday</span>
                  </div>
                </div>
              </div>
            )}

            {/* LIST VIEW (Accessible via toggle) */}
            {scheduleView === 'list' && (
              <div className="pt-4 divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50">
                {dayBreakdown.map((d, idx) => (
                  <div key={d.dateStr} className="p-3.5 flex items-center justify-between bg-white hover:bg-slate-50/80 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="w-6 text-center text-xs font-mono font-bold text-slate-400">#{idx + 1}</span>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{fmtDate(d.dateStr)}</p>
                        <p className="text-xs text-slate-500">{d.dayName} {d.isWeekend && <span className="text-amber-600 font-semibold">(Weekend)</span>}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">

                      {d.isPaid ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <FiCheck size={12} /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
                          <FiX size={12} /> LOP
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}


          </div>

          {/* Reason & Remarks Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <FiFileText className="text-sky-600" size={18} />
              <h3 className="font-bold text-slate-900 text-sm">Reason & Approval Remarks</h3>
            </div>

            {/* Applicant reason */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                <FiUser size={13} /> Applicant Reason
              </div>
              <p className="text-sm text-slate-700 leading-relaxed italic">
                {leave.reason ? `"${leave.reason}"` : 'No specific reason was provided by the applicant.'}
              </p>
            </div>

            {/* HR Remarks */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                <FiShield size={13} /> HR / Manager Remarks
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                {leave.remarks ? leave.remarks : 'No HR remarks recorded yet.'}
              </p>
            </div>

            {/* Penalty Banner if applied */}
            {leave.penaltyApplied && (
              <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 flex items-start gap-2.5 text-xs text-rose-900 leading-relaxed">
                <FiAlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={16} />
                <div>
                  <strong className="font-bold">Loss of Pay (LOP) Enforced:</strong> This leave application was rejected. Loss of Pay was assessed in payroll deductions.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Employee Leave Balance & History (5 cols) */}
        <div className="lg:col-span-5 space-y-6">

          {/* Real-time Employee Balance Snapshot */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FiLayers className="text-sky-600" size={18} />
                <h3 className="font-bold text-slate-900 text-sm">Leave Quota & Balance</h3>
              </div>
            </div>

            {balanceData ? (
              <div className="space-y-4">
                {/* Annual stats without Annual Limit */}
                <div>
                  <p className="text-[11px] font-bold text-sky-600 uppercase tracking-wider mb-2">Annual Overview</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase">Carry Forward</p>
                      <p className="text-lg font-black text-slate-900">{balanceData.totalCarryForward ?? balanceData.carryForward ?? 0}d</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase">Paid Taken YTD</p>
                      <p className="text-lg font-black text-slate-900">{balanceData.paidTakenYear ?? 0}d</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-emerald-700 uppercase">Remaining Paid</p>
                      <p className="text-lg font-black text-emerald-800">
                        {balanceData.totalPaidLeavesAvailableYear ?? balanceData.currentAvailableBalance ?? 0}d
                      </p>
                    </div>
                  </div>
                </div>

                {/* Monthly Quota */}
                <div>
                  <p className="text-[11px] font-bold text-sky-600 uppercase tracking-wider mb-2">This Month Quota</p>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase">Paid Used</p>
                      <p className="text-lg font-black text-slate-900">{balanceData.paidTakenMonth ?? 0}d</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase">Slots Available</p>
                      <p className="text-lg font-black text-slate-900">{balanceData.totalPaidLeavesAvailableMonth ?? 0}d</p>
                    </div>
                  </div>
                </div>

                {/* LOP summary */}
                {((balanceData.lopLeavesMonth || 0) > 0 || (balanceData.lopLeavesYear || 0) > 0) && (
                  <div className="rounded-xl p-4 text-white bg-slate-900">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                      <FiTrendingDown size={13} className="text-rose-400" /> Loss of Pay Summary
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-xl font-black">{balanceData.lopLeavesMonth ?? 0}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Month LOP</p>
                      </div>
                      <div className="border-x border-slate-800">
                        <p className="text-xl font-black">{balanceData.lopLeavesYear ?? 0}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Year LOP</p>
                      </div>
                      <div>
                        <p className="text-xl font-black">
                          {balanceData.lopDeductionAmount != null ? `₹${Number(balanceData.lopDeductionAmount).toLocaleString('en-IN')}` : '—'}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Est. Deduction</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-400 py-4 text-center">No balance quota records available</p>
            )}
          </div>

          {/* Other recent applications by this employee */}
          {balanceData && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 text-sm mb-3">Other Applications by {name.split(' ')[0]}</h3>
              
              {['pendingRequests', 'approvedRequests', 'rejectedRequests'].every(k => !(balanceData[k] || []).length) ? (
                <p className="text-xs text-slate-400 py-2">No other leave requests found for this year.</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {(balanceData.approvedRequests || []).slice(0, 3).map(l => (
                    <div
                      key={l.id}
                      onClick={() => navigate(`/hr/leave/${l.id}`)}
                      className="p-3 rounded-xl border border-slate-100 hover:border-sky-200 hover:bg-sky-50/40 transition-all flex items-center justify-between cursor-pointer"
                    >
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span className="text-xs font-bold text-slate-800">{l.leaveType}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(l.startDate)} &rarr; {fmtDate(l.endDate)}</p>
                      </div>
                      <span className="text-xs font-black text-slate-700">{l.daysCount}d &rarr;</span>
                    </div>
                  ))}

                  {(balanceData.pendingRequests || []).filter(l => l.id !== leave.id).map(l => (
                    <div
                      key={l.id}
                      onClick={() => navigate(`/hr/leave/${l.id}`)}
                      className="p-3 rounded-xl border border-amber-200/80 bg-amber-50/40 hover:bg-amber-50 transition-all flex items-center justify-between cursor-pointer"
                    >
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          <span className="text-xs font-bold text-amber-900">{l.leaveType} (Pending)</span>
                        </div>
                        <p className="text-[11px] text-amber-700/80 mt-0.5">{fmtDate(l.startDate)} &rarr; {fmtDate(l.endDate)}</p>
                      </div>
                      <span className="text-xs font-black text-amber-800">{l.daysCount}d &rarr;</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Confirmation Modal (Portal, No Blur, Centered) ── */}
      <ConfirmModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, status: null, loading: false })}
        onConfirm={handleAction}
        loading={confirm.loading}
        title={confirm.status === 'APPROVED' ? 'Approve Leave Application' : confirm.status === 'REJECTED' ? 'Reject Leave Application' : 'Reverse Rejection'}
        message={
          <div className="space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed">
              {confirm.status === 'APPROVED'
                ? `Confirm approval of ${leave.daysCount} day(s) leave for ${name}?`
                : confirm.status === 'REJECTED'
                ? `Reject leave for ${name}? Loss of Pay (LOP) will apply to unauthorized duration.`
                : `Reverse rejection for ${name}? This will restore the application to PENDING status and remove the LOP deduction.`}
            </p>
            {confirm.status !== 'REVERSE' && (
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Remarks / Notes (Optional)
                </label>
                <input
                  type="text"
                  placeholder="Add a remark for the employee..."
                  value={actionRemark}
                  onChange={e => setActionRemark(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl focus:outline-none focus:border-sky-500"
                />
              </div>
            )}
          </div>
        }
        confirmText={confirm.status === 'APPROVED' ? 'Confirm Approval' : confirm.status === 'REJECTED' ? 'Reject Application' : 'Reverse Rejection'}
        variant={confirm.status === 'APPROVED' ? 'info' : confirm.status === 'REVERSE' ? 'warning' : 'danger'}
      />
    </div>
  );
}
